import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useLabelTemplate, useUpdateLabelTemplate } from "@/hooks/useLabelTemplate";
import { getApiErrorMessage } from "@/lib/api/api";
import type { LabelFieldKey, LabelSizeKey } from "@/types/label-template";
import { LabelFieldPicker } from "./LabelFieldPicker";
import { LabelPreview } from "./LabelPreview";
import { LabelSizePicker } from "./LabelSizePicker";
import { sampleBatchId } from "./label-fields";

interface LabelSettingsCardProps {
  /** False renders the template read-only — a cook may see it, not change it. */
  canManage: boolean;
  /** Fixed so the preview does not change under a test or a screenshot. */
  today?: Date;
}

/**
 * Settings → Labels: the one place a label's fields are chosen.
 *
 * Before T-1001 this was configured per recipe, buried inside each recipe's
 * shelf life — "your label should be consistent; once you set it up it should
 * auto apply to every label". Saving here changes every recipe that has not
 * asked to be different, without anyone opening a recipe.
 */
export function LabelSettingsCard({ canManage, today }: LabelSettingsCardProps) {
  const { t } = useLingui();
  const { data, isLoading, isError, error } = useLabelTemplate();
  const save = useUpdateLabelTemplate();

  // Edits live here until saved; `data` is the truth until then.
  const [draft, setDraft] = useState<LabelFieldKey[] | null>(null);
  const [sizeDraft, setSizeDraft] = useState<LabelSizeKey | null>(null);
  // Reset the drafts when a different template arrives rather than in an effect.
  const [seenTemplate, setSeenTemplate] = useState<string | null>(null);
  const serverFields = data?.fields;
  const serverSize = data?.size;
  const serverKey = data ? `${data.fields.join(",")}|${data.size}` : null;
  if (serverKey !== seenTemplate) {
    setSeenTemplate(serverKey);
    setDraft(null);
    setSizeDraft(null);
  }

  const fields = draft ?? serverFields ?? [];
  const size = sizeDraft ?? serverSize;
  const isDirty =
    (draft != null && serverFields?.join(",") !== draft.join(",")) ||
    (sizeDraft != null && serverSize !== sizeDraft);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Labels`}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Labels`}</CardTitle>
          <CardDescription>
            {getApiErrorMessage(error, t`Could not load the label template.`)}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Labels`}</CardTitle>
        <CardDescription>
          {t`Choose what every printed label shows. Recipes follow this unless one is set up differently.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <LabelFieldPicker
            available={data.available}
            selected={fields}
            onChange={setDraft}
            disabled={!canManage || save.isPending}
          />
          <LabelSizePicker
            available={data.availableSizes ?? []}
            selected={size ?? "1x3"}
            onChange={setSizeDraft}
            disabled={!canManage || save.isPending}
          />
          {canManage && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() =>
                  save.mutate(
                    { fields, ...(size ? { size } : {}) },
                    {
                    onSuccess: () => {
                      setDraft(null);
                      setSizeDraft(null);
                      toast.success(t`Label template saved`);
                    },
                    onError: (err) =>
                      toast.error(getApiErrorMessage(err, t`Failed to save the label template`)),
                    },
                  )
                }
                disabled={!isDirty || save.isPending}
              >
                {save.isPending && <Loader2 className="size-4 animate-spin" />}
                {t`Save labels`}
              </Button>
              {isDirty && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDraft(null);
                    setSizeDraft(null);
                  }}
                  disabled={save.isPending}
                >
                  {t`Discard`}
                </Button>
              )}
            </div>
          )}
        </div>

        <LabelPreview
          fields={fields}
          title={t`Sample recipe`}
          values={{
            PREP_DATE: (today ?? new Date()).toLocaleDateString(),
            USE_BY: t`Set per recipe`,
            ALLERGENS: t`From the recipe`,
            STORAGE_METHOD: t`Refrigerated`,
            PREPARED_BY: t`Signed at print`,
            BATCH_ID: sampleBatchId("0000", today ?? new Date()),
            CONTAINER_FLAG: t`From the container`,
          }}
        />
      </CardContent>
    </Card>
  );
}
